import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const TABS = [
  { key: "All", label: "All" },
  { key: "Basketball", label: "Basketball" },
  { key: "Football", label: "Football" },
  { key: "Baseball", label: "Baseball" },
  { key: "Hockey", label: "Hockey" },
];

export default function SportsCards() {
  const [active, setActive] = useState("All");
  const [basketball, setBasketball] = useState([]);
  const [football, setFootball] = useState([]);
  const [baseball, setBaseball] = useState([]);
  const [hockey, setHockey] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/products", { params: { category: "Basketball" } }),
      api.get("/products", { params: { category: "Football" } }),
      api.get("/products", { params: { category: "Baseball" } }),
      api.get("/products", { params: { category: "Hockey" } }),
    ]).then(([b, f, bb, h]) => { setBasketball(b.data); setFootball(f.data); setBaseball(bb.data); setHockey(h.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const show = (arr, cat) => (active === "All" || active === cat) && arr.length > 0;
  const noneShown = basketball.length === 0 && football.length === 0 && baseball.length === 0 && hockey.length === 0;

  return (
    <div className="bg-background">
      {/* HERO */}
      <section className="grain relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1708983471447-931a5b164778?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="" aria-hidden="true" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/40" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-5 py-20 sm:px-8 md:py-24">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="overline text-secondary">Hardwood &amp; Gridiron</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-4 max-w-3xl font-display text-4xl font-900 leading-[0.95] tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Sports <span className="text-primary">Cards</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 max-w-lg text-base text-zinc-400">
            Basketball, football, baseball and hockey — rookies, autos, refractors and graded slabs. Authenticated and protected.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`sports-filter-${t.key.toLowerCase()}`}
              onClick={() => setActive(t.key)}
              className={`rounded-full border px-5 py-2 text-sm transition-colors ${
                active === t.key ? "border-primary bg-primary text-white" : "border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-4 pt-10 sm:gap-6 lg:grid-cols-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse rounded-2xl border border-white/10 bg-zinc-900" />
            ))}
          </div>
        ) : (
          <div className="space-y-14 pt-10">
            {show(basketball, "Basketball") && (
              <div data-testid="basketball-section">
                <h2 className="mb-6 font-display text-2xl font-800 tracking-tight text-white">Basketball</h2>
                <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
                  {basketball.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              </div>
            )}
            {show(football, "Football") && (
              <div data-testid="football-section">
                <h2 className="mb-6 font-display text-2xl font-800 tracking-tight text-white">Football</h2>
                <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
                  {football.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              </div>
            )}
            {show(baseball, "Baseball") && (
              <div data-testid="baseball-section">
                <h2 className="mb-6 font-display text-2xl font-800 tracking-tight text-white">Baseball</h2>
                <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
                  {baseball.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              </div>
            )}
            {show(hockey, "Hockey") && (
              <div data-testid="hockey-section">
                <h2 className="mb-6 font-display text-2xl font-800 tracking-tight text-white">Hockey</h2>
                <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
                  {hockey.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              </div>
            )}
            {noneShown && (
              <p className="py-20 text-center text-zinc-500" data-testid="sports-empty">
                No sports cards yet. <Link to="/catalog" className="text-secondary">Browse all cards</Link>
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
